/**
 * Shared inline SVG icons for the app.
 * Import from `icons` (or `icons/svg`) instead of defining icons in feature files.
 *
 * Usage:
 *   import { EyeIcon, TrashIcon } from "../../icons";
 *   <EyeIcon open size={18} />
 */
import React from "react";

function IconBase({
  size = 18,
  className,
  children,
  viewBox = "0 0 24 24",
  fill = "none",
  ...rest
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={fill}
      className={className}
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function EyeIcon({ open = true, size = 18, className, ...rest }) {
  if (open) {
    return (
      <IconBase size={size} className={className} {...rest}>
        <path
          d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      </IconBase>
    );
  }
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M3 3l18 18M10.6 10.6A3 3 0 0012 15a3 3 0 002.4-1.2M6.1 6.3C4 7.8 2.5 10 2 12c0 0 3.5 7 10 7 2 0 3.7-.5 5.1-1.2M17.9 15.7C20 14.2 21.5 12 22 12c0 0-3.5-7-10-7-1.1 0-2.1.2-3 .5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function DragHandleIcon({ size = 20, className, ...rest }) {
  return (
    <IconBase size={size} className={className} fill="currentColor" {...rest}>
      <circle cx="9" cy="7" r="1.75" />
      <circle cx="15" cy="7" r="1.75" />
      <circle cx="9" cy="12" r="1.75" />
      <circle cx="15" cy="12" r="1.75" />
      <circle cx="9" cy="17" r="1.75" />
      <circle cx="15" cy="17" r="1.75" />
    </IconBase>
  );
}

export function TrashIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

/** Pencil / edit — for Change actions. */
export function EditIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 6l3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function LockIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <rect
        x="5"
        y="11"
        width="14"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M8 11V8a4 4 0 118 0v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </IconBase>
  );
}

export function FrontIcon({ size = 14, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M12 4l6 6H8l4-6ZM6 14h12M6 18h12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function ForwardIcon({ size = 14, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M12 7l5 5H7l5-5ZM7 17h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function BackwardIcon({ size = 14, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M7 7h10M12 17l-5-5h10l-5 5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function BackIcon({ size = 14, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M6 6h12M6 10h12M12 20l-6-6h12l-6 6Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function FlipResetIcon({ size, className, ...rest }) {
  return (
    <IconBase
      size={size}
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      {...rest}
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <polyline points="3 3 3 8 8 8" />
    </IconBase>
  );
}

export function FlipHorizontalIcon({ size, className, ...rest }) {
  return (
    <IconBase
      size={size}
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      {...rest}
    >
      <polyline points="7 8 3 12 7 16" />
      <polyline points="17 8 21 12 17 16" />
      <line x1="3" y1="12" x2="21" y2="12" />
    </IconBase>
  );
}

export function FlipVerticalIcon({ size, className, ...rest }) {
  return (
    <IconBase
      size={size}
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      {...rest}
    >
      <polyline points="8 7 12 3 16 7" />
      <polyline points="8 17 12 21 16 17" />
      <line x1="12" y1="3" x2="12" y2="21" />
    </IconBase>
  );
}

export function PaddingTopIcon({ size, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <line
        x1="12"
        y1="6"
        x2="12"
        y2="18"
        stroke="currentColor"
        strokeWidth="2"
      />
      <polyline
        points="6 12 12 6 18 12"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </IconBase>
  );
}

export function PaddingRightIcon({ size, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <line
        x1="6"
        y1="12"
        x2="18"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
      />
      <polyline
        points="12 6 18 12 12 18"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </IconBase>
  );
}

export function PaddingBottomIcon({ size, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <line
        x1="12"
        y1="6"
        x2="12"
        y2="18"
        stroke="currentColor"
        strokeWidth="2"
      />
      <polyline
        points="6 12 12 18 18 12"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </IconBase>
  );
}

export function PaddingLeftIcon({ size, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <line
        x1="6"
        y1="12"
        x2="18"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
      />
      <polyline
        points="12 6 6 12 12 18"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </IconBase>
  );
}

export function ShadowToolIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M4 15h12a4 4 0 1 0-1-7.9 5 5 0 0 0-9 2A4 4 0 0 0 4 15z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M6 18h10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </IconBase>
  );
}

export function OutlineToolIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeDasharray="3 2"
      />
      <rect
        x="8"
        y="8"
        width="8"
        height="8"
        rx="1"
        fill="currentColor"
        opacity="0.15"
      />
    </IconBase>
  );
}

export function RotateToolIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M4 12a8 8 0 0114.2-5.1M20 4v5h-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 12a8 8 0 01-14.2 5.1M4 20v-5h5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </IconBase>
  );
}

export function WatermarkToolIcon({ size = 18, className, ...rest }) {
  return (
    <IconBase size={size} className={className} {...rest}>
      <path
        d="M7 17V7h6.2a3.4 3.4 0 0 1 0 6.8H7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M12 14l5 5M17 14v5h-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </IconBase>
  );
}
